import { centsToUnits, xopQuoteIsOpen, type XopQuote } from "@xop-any-time/shared";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Check, ClipboardList, Download, X } from "lucide-react";
import { useState, type JSX } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";

import { useAuth } from "../../../auth/AuthProvider.js";
import { NoAccessPanel } from "../../../components/ui/no-access-panel.js";
import { ApiError, isPermissionDenied } from "../../../lib/api.js";
import { keys } from "../../../lib/crmApi.js";
import { date, money, text } from "../../../lib/format.js";
import {
  acceptXopQuote,
  declineXopQuote,
  getXopQuote,
  xopQuoteKeys,
} from "../../../lib/xopQuotesApi.js";
import { PageHeader } from "../crm.js";
import {
  QuotePurchaseOrderDialog,
  type QuotePurchaseOrderDialogMode,
} from "../purchase-orders/QuotePurchaseOrderDialog.js";
import { XopQuoteStatusChip, decisionPhrase, validityPhrase } from "./quote-ui.js";
import { XopQuotePdfPreview, downloadXopQuotePdf } from "./xop-quote-pdf.js";

/**
 * One quote from XOP Networks, and the two things a reseller can do about it.
 *
 * THE DOCUMENT IS THE PDF, exactly as it is on the invoice page: a header
 * card with the facts a decision needs (number, status, validity, total) and
 * the whole document rendered inline by pdf.js underneath. The lines,
 * addresses and totals live in the file rather than being drawn twice — a
 * page that renders the same document in HTML and again as a PDF gives two
 * things to keep in step, and one of them will drift.
 *
 * Each fact is stated ONCE on the page for the same reason: the validity
 * phrase is the header card's own, and the strapline under the title says
 * only who asked and when. A figure printed twice is a figure that can be
 * read twice and believed once.
 *
 * ACCEPTING IS AN ORDER, so it goes through a confirmation that says so.
 * Declining takes an optional note that crm-api stores and emails to XOP
 * Networks (spec §4.3) — optional because a decline with no reason is still
 * a decline, and a required box only ever produces ".".
 *
 * A 409 IS NOT AN ERROR TO APOLOGISE FOR. It means the quote changed under
 * the page — accepted in another tab, or lapsed while it was open. The page
 * refetches and says what the quote is NOW, which is the only useful thing to
 * show someone whose button just did nothing. WHICH of those happened is
 * crm-api's to know, so the warning repeats crm-api's own sentence rather
 * than a paraphrase that has to cover every case at once.
 *
 * AFTER ACCEPTING, A THANK-YOU AND THE NEXT STEP (CEO, 2026-09-17). A
 * successful Accept opens `QuotePurchaseOrderDialog` in its `thanks` state:
 * XOP has been told, and here is a button that turns the quote into a
 * PURCHASE ORDER. An accepted quote then keeps pointing at its PO ("View
 * purchase order PO-…"), or — if the reseller said "Not now" — keeps offering
 * "Create purchase order", which opens the same dialog without the thanks.
 */

type Decision = "accept" | "decline";

function DecisionDialog({
  decision,
  quote,
  pending,
  onClose,
  onConfirm,
}: {
  decision: Decision | null;
  quote: XopQuote;
  pending: boolean;
  onClose: () => void;
  onConfirm: (note: string) => void;
}): JSX.Element {
  const [note, setNote] = useState("");
  const accepting = decision === "accept";

  return (
    <Dialog open={decision !== null} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{accepting ? "Accept this quote?" : "Decline this quote?"}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ fontSize: 14 }}>
          {accepting
            ? `Accepting ${quote.number ?? "this quote"} tells XOP Networks to place the order at ${money(centsToUnits(quote.totalCents), quote.currency)}. XOP Networks is notified straight away.`
            : `XOP Networks is told you have declined ${quote.number ?? "this quote"}. You can ask for a new quote at any time.`}
        </DialogContentText>
        {!accepting && (
          <TextField
            fullWidth
            multiline
            minRows={3}
            size="small"
            label="Why, if you would like to say (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            inputProps={{ maxLength: 2000 }}
            sx={{ mt: 2 }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose} disabled={pending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={accepting ? "success" : "error"}
          disabled={pending}
          onClick={() => onConfirm(note)}
        >
          {accepting ? "Accept the quote" : "Decline the quote"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function QuoteDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const quoteId = Number(id);
  // `/quotes/abc` gives NaN, which never becomes a request — so it must
  // never become a spinner either. See `MissingQuotePanel` below.
  const askable = Number.isInteger(quoteId) && quoteId > 0;
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [decision, setDecision] = useState<Decision | null>(null);
  // Open flag and mode apart, so the title does not flip from "Thank you" to
  // "Create…" while the dialog fades out.
  const [poDialogOpen, setPoDialogOpen] = useState(false);
  const [poDialogMode, setPoDialogMode] = useState<QuotePurchaseOrderDialogMode>("thanks");
  // Download fetches crm-api's rendered file; a refusal is said, not swallowed.
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const download = (target: XopQuote): void => {
    setDownloadError(null);
    downloadXopQuotePdf(target).catch((err: unknown) => {
      setDownloadError(err instanceof Error ? err.message : "The quote could not be downloaded.");
    });
  };
  const openPoDialog = (mode: QuotePurchaseOrderDialogMode): void => {
    setPoDialogMode(mode);
    setPoDialogOpen(true);
  };

  const { data, isPending, error } = useQuery({
    queryKey: xopQuoteKeys.detail(quoteId),
    queryFn: () => getXopQuote(quoteId),
    enabled: askable,
  });

  const decide = useMutation({
    mutationFn: ({ kind, note }: { kind: Decision; note: string }) =>
      kind === "accept" ? acceptXopQuote(quoteId) : declineXopQuote(quoteId, note),
    onSuccess: (_quote, { kind }) => {
      // Only a decision that LANDED is thanked — a 409 falls through to the
      // stale-quote warning instead.
      if (kind === "accept") openPoDialog("thanks");
    },
    onSettled: () => {
      // Settled, not success: after a 409 the page must show what the quote
      // IS, and that means refetching whether the decision landed or not.
      void queryClient.invalidateQueries({ queryKey: xopQuoteKeys.detail(quoteId) });
      void queryClient.invalidateQueries({ queryKey: ["xop-quotes"] });
      void queryClient.invalidateQueries({ queryKey: keys.overview });
      setDecision(null);
    },
  });

  if (isPermissionDenied(error)) {
    return <NoAccessPanel permission={error.permission ?? "invoices:view"} />;
  }

  // An id that is not a quote id is a quote that does not exist. The query is
  // disabled for one, so `isPending` below would stay true forever and the
  // route would spin on a spinner with nothing behind it.
  if (!askable) {
    return <MissingQuotePanel />;
  }

  if (isPending) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", py: 10 }}>
        <CircularProgress size={26} aria-label="Loading" />
      </Box>
    );
  }

  // `!data?.quote`, not `!data`: a 200 whose body carries no `quote` is a
  // body this page cannot render, and dereferencing it threw inside render
  // and took the route down. To a reseller that is the same fact as a 404 —
  // the quote is not there — so it is said the same way.
  if (error || !data?.quote) {
    const status = error instanceof ApiError ? error.status : 0;
    if (!error || status === 404) return <MissingQuotePanel />;
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <BackToQuotes />
        <Alert severity="error" data-testid="quote-load-error">
          {(error as Error).message || "That quote could not be loaded."}
        </Alert>
      </Box>
    );
  }

  const quote = data.quote;
  const open = xopQuoteIsOpen(quote);
  const decided = decisionPhrase(quote);
  const staleDecision =
    decide.error instanceof ApiError && decide.error.code === "QUOTE_NOT_OPEN";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <BackToQuotes />

      <PageHeader
        title={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <span>Quote {quote.number ?? `#${quote.id}`}</span>
            <XopQuoteStatusChip status={quote.effectiveStatus} />
          </Box>
        }
        description={[
          quote.customerName
            ? `For ${quote.customerName} · requested by ${text(quote.requestedByEmail)} on ${date(quote.issuedAt)}`
            : `Requested by ${text(quote.requestedByEmail)} on ${date(quote.issuedAt)}`,
          // THE DOCUMENT IS THE PAGE (2026-09-14): the header and the PDF are
          // all there is. The total, the dates and the reseller's note are on
          // the document itself; the decision — the one thing the PDF cannot
          // carry, since it is issued before it is made — rides on this line.
          validityPhrase(quote),
          decided,
          quote.decisionNote?.trim() ? quote.decisionNote.trim() : null,
        ]
          .filter((part): part is string => Boolean(part))
          .join(" · ")}
        actions={
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<Download size={16} />}
              onClick={() => download(quote)}
            >
              Download
            </Button>
            {/* The purchase order made from an accepted quote (2026-09-17):
                a link to it when there is one, the offer to make one when
                there is not. */}
            {quote.effectiveStatus === "accepted" && quote.purchaseOrder && (
              <Button
                component={RouterLink}
                to={`/purchase-orders/${quote.purchaseOrder.id}`}
                variant="contained"
                startIcon={<ClipboardList size={16} />}
                data-testid="quote-view-po"
              >
                View purchase order {quote.purchaseOrder.number ?? ""}
              </Button>
            )}
            {quote.effectiveStatus === "accepted" && !quote.purchaseOrder && can("invoices:edit") && (
              <Button
                variant="contained"
                startIcon={<ClipboardList size={16} />}
                onClick={() => openPoDialog("create")}
                data-testid="quote-create-po"
              >
                Create purchase order
              </Button>
            )}
            {/* A COURTESY, not a gate — crm-api refuses the POST without
                `invoices:edit`. See `auth/usePermissions.ts`. */}
            {open && can("invoices:edit") && (
              <>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<X size={16} />}
                  onClick={() => setDecision("decline")}
                >
                  Decline
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<Check size={16} />}
                  onClick={() => setDecision("accept")}
                >
                  Accept
                </Button>
              </>
            )}
          </Box>
        }
      />

      {/* THE SERVER'S SENTENCE, not the page's paraphrase. crm-api knows
          which of the several ways a quote stops being open actually
          happened — accepted in another tab, declined by a colleague,
          lapsed at midnight — and only its message can say which. The
          refetch above (`onSettled`) is what makes the second line true. */}
      {staleDecision && (
        <Alert severity="warning" data-testid="quote-stale">
          {(decide.error as ApiError).message || "This quote is no longer open."} The current
          state is shown above.
        </Alert>
      )}

      {decide.error && !staleDecision && !isPermissionDenied(decide.error) && (
        <Alert severity="error" data-testid="quote-decision-error">
          {(decide.error as ApiError).message}
        </Alert>
      )}

      {isPermissionDenied(decide.error) && (
        <NoAccessPanel permission={decide.error.permission ?? "invoices:edit"} />
      )}

      {downloadError && (
        <Alert severity="error" data-testid="quote-download-error">
          {downloadError}
        </Alert>
      )}

      <Card>
        <CardContent>
          <XopQuotePdfPreview quote={quote} />
        </CardContent>
      </Card>

      <DecisionDialog
        decision={decision}
        quote={quote}
        pending={decide.isPending}
        onClose={() => setDecision(null)}
        onConfirm={(note) => decide.mutate({ kind: decision ?? "decline", note })}
      />

      <QuotePurchaseOrderDialog
        open={poDialogOpen}
        mode={poDialogMode}
        quote={{ id: quote.id, number: quote.number }}
        onClose={() => setPoDialogOpen(false)}
      />
    </Box>
  );
}

/**
 * "The quote is not there", in the three ways it can be true: a 404, a 200
 * with no quote in it, and an id that was never a quote id. A reseller is
 * owed the same sentence for all three — WHY the portal cannot show it is
 * the portal's problem, not theirs.
 */
function MissingQuotePanel(): JSX.Element {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <BackToQuotes />
      <Alert severity="info" data-testid="quote-not-found">
        That quote could not be found. It may belong to another reseller, or it may have been
        withdrawn.
      </Alert>
    </Box>
  );
}

function BackToQuotes(): JSX.Element {
  return (
    <Box>
      <Button component={RouterLink} to="/quotes" color="inherit" startIcon={<ArrowLeft size={16} />}>
        Back to Quotes
      </Button>
    </Box>
  );
}

export default QuoteDetailPage;
